import { useTranslation } from 'react-i18next';

import type { Invoice } from '~/api/invoices/types';

import { AlertDialog, Button, Menu } from '~/components';
import { optionsMenuTriggerStyle } from '~/components/Table/TableRow.css';
import { isApproved, isNotApproved, isNotPaidYet } from '~/features/documents';

import {
  useOnCreditInvoice,
  useOnDeleteInvoice,
  useOnDownloadFcInvoice,
  useOnDuplicateInvoice,
  useOnEditInvoice,
  useOnResendCoInvoice,
  useOnShowInvoice,
  useOnViewInvoice,
} from '../../../invoices.hooks';
import { ConfirmDeleteInvoiceDialog } from '../../ConfirmDeleteInvoiceDialog';
import { ConfirmDownloadFcInvoiceDialog } from '../../ConfirmDownloadFcInvoiceDialog';
import { ConfirmResendCoInvoiceDialog } from '../../ConfirmResendCoInvoiceDialog';
import { InvoiceAlreadyApprovedDialog } from '../../InvoiceAlreadyApprovedDialog';
import { InvoiceConfirmEditDialog } from '../../InvoiceConfirmEditDialog';
import { InvoicePreviewDialog } from '../../InvoicePreviewDialog';

type Props = {
  invoice: Invoice;
};

export const InvoiceOptionsCell = ({ invoice }: Props) => {
  const { onShow } = useOnShowInvoice(invoice);
  const { alreadyApprovedHandle, confirmEdit, confirmEditHandle, isPending: isEditPending, onEdit } = useOnEditInvoice(invoice);
  const { data: previewData, onPreview, reset: resetPreviewMutation } = useOnViewInvoice(invoice);
  const { onDuplicate } = useOnDuplicateInvoice(invoice);
  const { confirmDownload, downloadHandle, isPending: isDownloadPending, onDownload } = useOnDownloadFcInvoice(invoice);
  const { confirmResend, isPending: isResendPending, onResend: onResendCoInvoice, resendHandle } = useOnResendCoInvoice(invoice);
  const { onCredit } = useOnCreditInvoice(invoice);
  const { confirmDelete, deleteHandle, isPending: isDeletePending, onDelete } = useOnDeleteInvoice(invoice);

  const { t } = useTranslation(['common', 'invoices']);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Menu.Root>
        <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} className={optionsMenuTriggerStyle} icon="MoreVert" />} />

        <Menu.List>
          {invoice.status !== 'Draft' && (
            <Menu.Item icon="File" onClick={onShow}>
              {t('invoices:overview.rowActions.details')}
            </Menu.Item>
          )}

          {isNotApproved(invoice) && (
            <Menu.Item icon="Edit" onClick={onEdit}>
              {t('invoices:overview.rowActions.edit.label')}
            </Menu.Item>
          )}

          <Menu.Item icon="Visibility" onClick={onPreview}>
            {t('invoices:overview.rowActions.preview')}
          </Menu.Item>

          {invoice.status !== 'Draft' && (
            <Menu.Item icon="EditDocument" onClick={onDuplicate}>
              {t('invoices:overview.rowActions.duplicate')}
            </Menu.Item>
          )}

          {isApproved(invoice) && (
            <Menu.Item icon="Download" onClick={onDownload}>
              {t('invoices:overview.rowActions.download')}
            </Menu.Item>
          )}

          {isNotPaidYet(invoice) && invoice.coTransportType !== 'Peppol' && (
            <Menu.Item icon="Send" onClick={onResendCoInvoice}>
              {t('invoices:overview.rowActions.resendCoInvoice')}
            </Menu.Item>
          )}

          {isNotPaidYet(invoice) && (
            <Menu.Item icon="CreditNote" onClick={onCredit}>
              {t('invoices:overview.rowActions.credit')}
            </Menu.Item>
          )}

          {invoice.status === 'Draft' && (
            <Menu.Item icon="Trash" onClick={onDelete}>
              {t('common:actions.delete')}
            </Menu.Item>
          )}
        </Menu.List>
      </Menu.Root>

      <AlertDialog.Root handle={confirmEditHandle}>
        <InvoiceConfirmEditDialog isLocked={invoice.status === 'LockedByUser'} loading={isEditPending} onConfirm={confirmEdit} />

        <AlertDialog.Root handle={alreadyApprovedHandle}>
          <InvoiceAlreadyApprovedDialog invoice={invoice} onClose={() => confirmEditHandle.close()} />
        </AlertDialog.Root>
      </AlertDialog.Root>

      <AlertDialog.Root handle={downloadHandle}>
        <ConfirmDownloadFcInvoiceDialog
          isPeppol={invoice.fcTransportType === 'Peppol'}
          loading={isDownloadPending}
          onConfirm={confirmDownload}
        />
      </AlertDialog.Root>

      <AlertDialog.Root handle={resendHandle}>
        <ConfirmResendCoInvoiceDialog invoice={invoice} loading={isResendPending} onConfirm={confirmResend} />
      </AlertDialog.Root>

      <AlertDialog.Root handle={deleteHandle}>
        <ConfirmDeleteInvoiceDialog loading={isDeletePending} onConfirm={confirmDelete} />
      </AlertDialog.Root>

      <InvoicePreviewDialog html={previewData} onClose={resetPreviewMutation} title={invoice.title} />
    </div>
  );
};
